// Campaign ending — three background "cuts" with narration, reached right
// after BELA_OUTRO. The closing bookend to PROLOGUE_LINES (act1.ts): the
// final line ("그 후로도 오래, 등불은 꺼지지 않았다.") is a "happily ever after"
// beat told through the game's own 빛/등불 motif rather than saying it
// outright (user: avoid the literal "행복하게 살았다" cliché). Assets from
// `assets/generated/othellos/ending/` (PNG→webp q85) + `ui/ending-villager-
// *.webp` (already-transparent cutouts, PNG→webp q90) — three villagers
// (woman left, elder right, child centre) fade in one after another over
// the third cut only.

import type { EndingCut, EndingOverlay } from "./Ending.tsx";
import type { Loc } from "./loc.ts";

export const ENDING_REALM_BG =
  "/assets/backgrounds/ending-1-restored-realm.webp";
export const ENDING_FOREST_BG =
  "/assets/backgrounds/ending-2-renewed-forest.webp";
export const ENDING_VILLAGE_BG =
  "/assets/backgrounds/ending-3-lantern-village.webp";
export const ENDING_VILLAGER_WOMAN = "/assets/ui/ending-villager-woman.webp";
export const ENDING_VILLAGER_ELDER = "/assets/ui/ending-villager-elder.webp";
export const ENDING_VILLAGER_CHILD = "/assets/ui/ending-villager-child.webp";

/** Woman (left) → elder (right) → child (centre, smaller/closer), each
 *  appearing ~500ms after the last so the group reads as gathering slowly,
 *  not popping in all at once. */
export const ENDING_VILLAGERS: readonly EndingOverlay[] = [
  {
    src: ENDING_VILLAGER_WOMAN,
    // Kept at the original (larger) size; pushed left of the frame edge so
    // the wide cutout still clears the elder instead of shrinking to fit.
    style: { left: "-20%", height: "62%" },
    delayMs: 0,
  },
  {
    src: ENDING_VILLAGER_ELDER,
    // Mirrored so he faces inward (toward the woman/child) instead of off-frame.
    style: { right: "-18%", height: "60%", transform: "scaleX(-1)" },
    delayMs: 500,
  },
  {
    src: ENDING_VILLAGER_CHILD,
    style: { left: "50%", height: "44%", transform: "translateX(-50%)" },
    delayMs: 1000,
  },
];

/** Cut 1 — the castle/throne room where 벨라 fell, now quiet and restored.
 *  Cut 2 — the wider 베르다 안개숲 healing, rift-fog replaced by clear water
 *  and green. Cut 3 — the hero's own 등불 마을 (lost in the backstory,
 *  PROLOGUE_LINES line 1), lit again; villagers celebrate on the path. */
const L = (ko: string, en: string): Loc => ({ ko, en });

export const ENDING_CUTS: readonly EndingCut[] = [
  {
    bg: ENDING_REALM_BG,
    lines: [
      L("봉인의 심장은 다시 고요히 뛰기 시작했다.", "The Seal Heart began beating quietly once more."),
      L("균열빛은 걷혔고, 왕좌엔 아무도 남지 않았다.", "The rift-light had cleared, and the throne stood empty."),
    ],
  },
  {
    bg: ENDING_FOREST_BG,
    lines: [
      L("베르다 안개숲의 균열도 하나씩 아물어 갔다.", "The rifts across the Verda Mistwood healed, one by one."),
      L("짙은 안개 대신, 맑은 시내와 초록이 돌아왔다.", "In place of the thick fog, clear streams and green returned."),
    ],
  },
  {
    bg: ENDING_VILLAGE_BG,
    overlays: ENDING_VILLAGERS,
    lines: [
      L("그리고 마침내, 등불 마을에도 다시 불이 켜졌다.", "And at last, the lanterns lit again in the village I'd lost."),
      L("떠났던 이가 돌아왔다. 그 후로도 오래, 등불은 꺼지지 않았다.", "The one who'd left came home. And for long after, the lanterns never went dark."),
    ],
  },
];
